import type {
  DraftSurveyTemplateCondition,
  DraftSurveyTemplateQuestion
} from "../../types";
import type { Answers } from "./types";

const isConditionTriggered = (
  condition: DraftSurveyTemplateCondition,
  answers: Answers,
  questionMap: Map<string, DraftSurveyTemplateQuestion>
): boolean => {
  const parentQ = questionMap.get(condition.parent_question_internal_id);
  if (!parentQ) return false;

  const answer = answers[parentQ.internal_id];
  const condType = condition.condition.type as string | undefined;

  switch (condType) {
    case "filled_in":
      if (!answer) return false;
      return answer.length > 0;
    case "empty":
      if (!answer) return true;
      return answer.length === 0;
    case "selected": {
      if (!answer) return false;
      const target = condition.condition.answer as string;
      return Array.isArray(answer)
        ? answer.includes(target)
        : answer === target;
    }
    default:
      return false;
  }
};

const buildSkipReason = (
  condition: DraftSurveyTemplateCondition,
  parentQ: DraftSurveyTemplateQuestion
): string => {
  const parentLabel = parentQ.question || `Question ${parentQ.index + 1}`;
  const condType = condition.condition.type as string;

  if (condition.action === "end_survey") {
    if (condType === "selected") {
      const answer = condition.condition.answer as string;
      return `Survey ended because "${parentLabel}" was "${answer}"`;
    }
    return condType === "empty"
      ? `Survey ended because "${parentLabel}" was empty`
      : `Survey ended because "${parentLabel}" was filled in`;
  }

  if (condType === "selected") {
    const answer = condition.condition.answer as string;
    return `Hidden because "${parentLabel}" was "${answer}"`;
  }
  return condType === "empty"
    ? `Hidden because "${parentLabel}" was empty`
    : `Hidden because "${parentLabel}" was filled in`;
};

type SkipReasons = Map<string, string>;

const applyHideAction = (
  c: DraftSurveyTemplateCondition,
  parentQ: DraftSurveyTemplateQuestion,
  hiddenIds: Set<string>,
  skipReasons: SkipReasons
) => {
  for (const id of c.child_question_internal_ids) {
    hiddenIds.add(id);
    if (!skipReasons.has(id)) skipReasons.set(id, buildSkipReason(c, parentQ));
  }
};

const applyEndSurveyAction = (
  c: DraftSurveyTemplateCondition,
  parentQ: DraftSurveyTemplateQuestion,
  questions: DraftSurveyTemplateQuestion[],
  endSurveyAfterIds: Set<string>,
  skipReasons: SkipReasons
) => {
  endSurveyAfterIds.add(c.parent_question_internal_id);
  const parentIdx = parentQ.index;
  for (const q of questions)
    if (q.index > parentIdx && !skipReasons.has(q.internal_id))
      skipReasons.set(q.internal_id, buildSkipReason(c, parentQ));
};

export const evaluateConditions = (
  questions: DraftSurveyTemplateQuestion[],
  conditions: DraftSurveyTemplateCondition[],
  answers: Answers
): {
  visibleQuestions: DraftSurveyTemplateQuestion[];
  endSurveyAfterIds: Set<string>;
  skipReasons: SkipReasons;
} => {
  const questionMap = new Map(questions.map((q) => [q.internal_id, q]));
  const hiddenIds = new Set<string>();
  const endSurveyAfterIds = new Set<string>();
  const skipReasons: SkipReasons = new Map();

  for (const c of conditions) {
    if (!isConditionTriggered(c, answers, questionMap)) continue;
    const parentQ = questionMap.get(c.parent_question_internal_id);
    if (!parentQ) continue;

    if (c.action === "hide")
      applyHideAction(c, parentQ, hiddenIds, skipReasons);
    else if (c.action === "end_survey")
      applyEndSurveyAction(
        c,
        parentQ,
        questions,
        endSurveyAfterIds,
        skipReasons
      );
  }

  return {
    visibleQuestions: questions.filter((q) => !hiddenIds.has(q.internal_id)),
    endSurveyAfterIds,
    skipReasons
  };
};
